/**
 * core-ui 의 native fetch wrapper.
 *
 * - zero runtime dependency (native fetch 만 사용)
 * - error 정규화 (network / http / parse / abort)
 * - AbortSignal 전달 (호출자가 cleanup)
 *
 * # credentials 정책
 *
 * `credentials: 'same-origin'` (browser default) — cross-origin 요청 시 cookie 전송 안 함.
 *
 * 명시적 선택 이유: cx-display-api 같은 upstream 은 `access-token` 헤더로 인증
 * (URL_TABLE 의 headers 가 운반). cookie 의존 없음.
 *
 * `'include'` 로 두면 CORS preflight 가 strict 해져 서버가
 * `Access-Control-Allow-Credentials: true` + 명시적 Origin 응답 필요 → wildcard
 * 허용 환경에선 CORS 차단. token 헤더만 보내려면 same-origin 으로 충분.
 *
 * 향후 cookie 기반 인증 도메인 추가 시 EndpointConfig 에 `credentials` 옵션 추가 검토.
 *
 * @see plan/category-widget-design.md — Q14 (FetchError shape), Q16 (abort 처리)
 */

import type { FetchError } from '../widgets/CoreUIDataProvider/types';
import type { EndpointConfig } from '../widgets/CoreUIDataProvider/url-table';

// public re-export — host adapter / 외부 consumer 가 fetcher 시그니처 작성 시 사용.
export type { EndpointConfig } from '../widgets/CoreUIDataProvider/url-table';

/**
 * abort 를 발생시키는 DOMException. 호출자가 catch 후 silent 처리.
 *
 * 일반 FetchError 와 구분하기 위해 별도 sentinel 객체 사용.
 */
export const ABORT_SIGNAL = Symbol('core-ui:fetch:abort');

export type FetchResult<T> = { kind: 'success'; data: T } | { kind: 'error'; error: FetchError } | { kind: 'abort' };

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

/**
 * 정규화된 fetch 호출.
 *
 * - HTTP 4xx/5xx → `{ kind: 'error', error: { kind: 'http', status } }`
 * - network 실패 → `{ kind: 'error', error: { kind: 'network', cause } }`
 * - parse 실패 → `{ kind: 'error', error: { kind: 'parse', cause } }`
 * - abort → `{ kind: 'abort' }`
 *
 * 에러 자체는 throw 하지 않고 result 로 반환 — caller 가 분기 처리.
 */
export async function fetcher<T>(config: EndpointConfig, signal: AbortSignal): Promise<FetchResult<T>> {
  let response: Response;

  try {
    response = await fetch(config.url, { method: 'GET', headers: config.headers, signal });
  } catch (rawError) {
    return isAbortError(rawError)
      ? { kind: 'abort' }
      : { kind: 'error', error: { kind: 'network', cause: toError(rawError) } };
  }

  if (!response.ok) return { kind: 'error', error: { kind: 'http', status: response.status } };

  try {
    const data = (await response.json()) as T;
    return { kind: 'success', data };
  } catch (rawError) {
    return isAbortError(rawError)
      ? { kind: 'abort' }
      : { kind: 'error', error: { kind: 'parse', cause: toError(rawError) } };
  }
}
